import React, { useCallback, useMemo, type PropsWithChildren, type SyntheticEvent } from 'react';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import startWithTemplateSpotlightImage from './assets/start-with-template-spotlight.png';
import { canMigrateToNewSpotlightPackage } from './common/canMigrateToNewSpotlightPackage.tsx';
import { START_WITH_TEMPLATE_SPOTLIGHT } from './controllers/constants.tsx';
import { useStartWithTemplateSpotlight } from './useStartWithTemplateSpotlight.tsx';
import { ConditionalSpotlightTarget } from './ui/ConditionalSpotlightTarget.tsx';
import { ConditionalSpotlightTransition } from './ui/ConditionalSpotlightTransition.tsx';

const spotlightHeading = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.heading',
	defaultMessage: 'Start with a template',
	description: 'Heading for the Start with a template spotlight',
});

const spotlightDescription = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.description',
	defaultMessage: 'Create a view based on popular prioritization frameworks.',
	description: 'Description for the Start with a template spotlight',
});

const spotlightTryItNowButton = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now',
	defaultMessage: 'Try it now',
	description: 'Primary action button for the Start with a template spotlight',
});

const spotlightDismissButton = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.dismiss',
	defaultMessage: 'Dismiss',
	description: 'Secondary dismiss button for the Start with a template spotlight',
});

const DISMISS_CLICK_IDENTIFIER_BY_ACTION = {
	tryItNow: 'try-it-now',
	dismiss: 'dismiss',
	close: 'close',
} as const;

type StartWithTemplateSpotlightProps = PropsWithChildren<{
	onTryItNow?: () => void;
	spotlightTargetNode?: HTMLElement | null;
	isDelayEligible?: boolean;
}>;

export const StartWithTemplateSpotlight = ({
	children,
	onTryItNow,
	spotlightTargetNode,
	isDelayEligible = true,
}: StartWithTemplateSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		isSpotlightVisible,
		dismissSpotlight,
		renderInPostOfficeMessageInstance,
		spotlightMessageId,
	} = useStartWithTemplateSpotlight({
		isDelayEligible,
	});

	const handleTryItNow = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'startWithTemplateSpotlightTryItNow');
			dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.tryItNow);
			onTryItNow?.();
		},
		[onTryItNow, dismissSpotlight],
	);

	const handleDismiss = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'startWithTemplateSpotlightDismiss');
			dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.dismiss);
		},
		[dismissSpotlight],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCloseButtonDismiss = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({ action: 'dismissed', actionSubject: 'jpdSpotlight' }), {
			activeSpotlight: 'startWithTemplateSpotlight',
		});
		dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.close);
	}, [createAnalyticsEvent, dismissSpotlight]);

	const actions = useMemo(() => {
		if (
			!fg('post-office_enable_jpd-juno_headless-spotlights') &&
			!fg('pol-21470-juno-spotlights-migration')
		) {
			return [
				{
					onClick: handleTryItNow,
					text: formatMessage(spotlightTryItNowButton),
					ctaType: 'primary' as const,
					testId: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now-button',
				},
				{
					onClick: handleDismiss,
					text: formatMessage(spotlightDismissButton),
					ctaType: 'secondary' as const,
					appearance: 'subtle' as const,
					testId: 'polaris.lib-onboarding-flows.start-with-template-spotlight.dismiss-button',
				},
			];
		}

		return [
			{
				onClick: handleTryItNow,
				text: formatMessage(spotlightTryItNowButton),
				ctaType: 'primary' as const,
				testId: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now-button',
			},
		];
	}, [handleDismiss, handleTryItNow, formatMessage]);

	if (!isSpotlightVisible) {
		return <>{children}</>;
	}

	const spotlightContent = (
		<ConditionalSpotlightTransition>
			<JiraSpotlight
				actions={actions}
				dialogPlacement="bottom left"
				dialogWidth={275}
				target={START_WITH_TEMPLATE_SPOTLIGHT}
				targetNode={spotlightTargetNode ?? undefined}
				key={START_WITH_TEMPLATE_SPOTLIGHT}
				heading={formatMessage(spotlightHeading)}
				targetRadius={3}
				targetBgColor={token('elevation.surface')}
				messageId={spotlightMessageId}
				messageType="engagement"
				testId="lib-onboarding-flows.start-with-template-spotlight"
				image={startWithTemplateSpotlightImage}
				shouldWatchTarget
				shouldObserveRootForTarget
				isMigrated={canMigrateToNewSpotlightPackage()}
				onDismiss={
					fg('post-office_enable_jpd-juno_headless-spotlights') ||
					fg('pol-21470-juno-spotlights-migration')
						? onCloseButtonDismiss
						: undefined
				}
			>
				{formatMessage(spotlightDescription)}
			</JiraSpotlight>
		</ConditionalSpotlightTransition>
	);

	return (
		<>
			<ConditionalSpotlightTarget name={START_WITH_TEMPLATE_SPOTLIGHT}>
				{children}
			</ConditionalSpotlightTarget>
			{renderInPostOfficeMessageInstance(spotlightContent)}
		</>
	);
};
